// Analytics
import {
  logRoofingQuestions,
  logPhotoUpload,
  logShingleColor,
  logRoofingComponents,
  logContactInfo,
  logRequestSent
} from "./analytics";

// Components
import FormWrapper from "components/ComponentLibrary/forms/oc-form-wrapper";
import ShingleColorSelector from "./FormSteps/ShingleColorSelector";
import QuotingWidgetContact from "./FormSteps/Contact";
import QuotingFormSuccess from "./FormSteps/Success";

// Modals
import RoofComplexityInfoModal from "./modals/RoofComplexityInfoModal";
import RoofingComponentsModal from "./modals/RoofingComponentsModal";

const QuotingWidgetForm = ({ clientZipCode, configuration, prefilledData, toggleImage, showStartPage, serviceArea, ...props }) => {
  const formParams = {
    uid: "quoting-widget",
    serviceArea: serviceArea,
    action: "/api/v2/forms/quoting-widget",
    method: "post",
    themeColor: props.themeColor,
    pages: [
      {
        progressLabel: "Your Home",
        title: "Get an Online",
        titlePinkText: "Roof Approximation",
        description: "This is not an official quote. This tool is used to help you prepare for the price range of your roofing project. Contact us to provide you with an official quote.",
        hideBorder: true,
        fields: [
          {
            label: "How many square feet is your home?",
            name: "area",
            helperText: "Not including a basement",
            helperTextColor: "#6D6D6D",
            min: 1,
            required: true,
            inputType: "number"
          },
          {
            label: "How many stories is your home?",
            name: "stories",
            options: [
              { text: "1", value: "1" },
              { text: "2", value: "2" },
              { text: "3", value: "3" }
            ],
            required: true,
            helperText: "Not including a basement",
            helperTextColor: "#6D6D6D",
            inputType: "radio",
            vertical: true,
            extendGap: true,
            margin: "32px",
          },
          {
            infoModalContent: RoofComplexityInfoModal,
            inputType: "radio",
            label: "What level of complexity is your roof?",
            name: "complexity",
            options: [
              { text: "Simple", value: "simple" },
              { text: "Moderate", value: "moderate" },
              { text: "Complex", value: "complex" }
            ],
            vertical: true,
            labelMargin: "0px",
            required: true,
            helperText: "",
            extendGap: true,
            margin: "32px",
          },
          {
            name: "contractor-account-id", //TODO: Remove hard coded account id once Cochran starts always using configurator
            inputType: "text",
            value: configuration?.accountId ? configuration.accountId : "f2170a1f-260c-e811-810b-5065f38b81e1",
            hidden: true
          },
        ],
        componentProps: { configuration: configuration },
        nextText: "Start",
        onNext: () => {
          logShingleColor();
          toggleImage();
        }
      },
      {
        progressLabel: "Materials",
        title: "Explore Roofing Materials",
        description: "It takes more than shingles to protect your home. The Total Protection Roofing System®^ is designed to help perform in three critical areas – SEAL. DEFEND. BREATHE.®",
        hideBorder: true,
        fields: [
          { name: "shingle-color" }
        ],
        component: ShingleColorSelector,
        componentProps: {
          configuration: configuration,
          subCta: {
            text: "What Are Roofing Components?",
            infoModalContent: RoofingComponentsModal,
            data: {
              track: "learn-more",
              trackDestination: "What are Roofing Components"
            }
          },
        },
        onPrevious: () => {
          logRoofingQuestions();
          toggleImage();
        },
        onNext: logShingleColor,
        disclaimers: [
          "^The Total Protection Roofing System® excludes non-Owens Corning™ Roofing Products such as flashing, fasteners and wood decking.",
          "We strive to accurately reproduce all photographs of shingles in literature and for onscreen viewing. The roofing product colors you see are as accurate as technology allows. We suggest that you view a roofing display or several shingles to get a better idea of the actual color. To accurately judge your shingle and color choice, we recommend that you view it on an actual roof with a pitch similar to your own roof prior to making your final color selection."
        ]
      },
      {
        progressLabel: "Contact Info",
        title: "Contact Information",
        hideBorder: true,
        fields: [
          { name: "first-name", labelMargin: "0px", required: true, value: prefilledData?.contact?.firstName, requiredError: "Please complete required field" },
          { name: "last-name", labelMargin: "0px", required: true, value: prefilledData?.contact?.lastName, requiredError: "Please complete required field" },
          { name: "phone", labelMargin: "0px", value: prefilledData?.contact?.phone, required: false },
          { name: "email", labelMargin: "0px", required: true, value: prefilledData?.contact?.email, requiredError: "Please complete required field" },
          { name: "address1", value: prefilledData?.contact?.address1, required: true, requiredError: "Please complete required field" },
          { name: "consent-to-provide-info-to-oc", required: true, requiredError: "Agreement to allow Owens Corning to process this data is required." },
          { name: "consent-to-provide-info-to-contractor", required: true, requiredError: "Agreement to allow contractor receiving this data is required." },
          { name: "consent-to-product-service-material" }
        ],
        component: QuotingWidgetContact,
        componentProps: {
          serviceArea: serviceArea,
          clientZipCode: clientZipCode,
          columns: true,
          phoneRequired: false,
          consentRequired: true,
          contractorConsentRequest: true,
          contractorConsentLabel: `By submitting, I am aware that ${configuration?.companyName || 'this contractor'} is an independent contractor and not an affiliate of Owens Corning and Asphalt, LLC or its affiliated companies.`
        },
        onPrevious: logShingleColor,
        nextText: "View Results",
        onNext: logRequestSent
      }
    ],
    successComponent: QuotingFormSuccess,
    successComponentProps: { configuration: configuration }
  }

  if (configuration?.id) {
    formParams.pages[0].fields.push(
      {
        name: "configuration-id",
        inputType: "text",
        value: configuration.id,
        hidden: true
      }
    )
  }

  if (!Object.keys(configuration).length || configuration["noComponentProductsShown"]) {
    formParams.pages = formParams.pages.filter(page => (page.id != "roofing-components"));
  }

  return (
    <FormWrapper clientZipCode={clientZipCode} configuration={configuration} showStartPage={showStartPage} { ...formParams } />
  );
};

export default QuotingWidgetForm;
